export const getOptionValue = (rawValue) => {
  if (rawValue && typeof rawValue === 'object' && Object.prototype.hasOwnProperty.call(rawValue, 'value')) {
    return rawValue.value;
  }

  return rawValue ?? null;
};

const toComparable = (value) => String(value ?? '').trim().toLowerCase();

const collectCandidates = (rawValue) => {
  if (Array.isArray(rawValue)) {
    return rawValue.flatMap((item) => collectCandidates(item));
  }

  if (rawValue && typeof rawValue === 'object') {
    const meta = rawValue.meta || {};

    return [
      rawValue.value,
      rawValue.id,
      rawValue.code,
      rawValue.name,
      rawValue.label,
      meta.value,
      meta.id,
      meta.code,
      meta.name,
      meta.label,
    ];
  }

  return [rawValue];
};

const normalizeComparableList = (values) => {
  const comparable = values
    .map((value) => toComparable(value))
    .filter(Boolean);

  return Array.from(new Set(comparable));
};

export const findOptionByRawValue = (
  options,
  rawValue,
  {
    optionKeys = [],
    metaKeys = [],
  } = {}
) => {
  const candidates = normalizeComparableList(collectCandidates(rawValue));
  if (candidates.length === 0) {
    return null;
  }

  const list = Array.isArray(options) ? options : [];

  return list.find((option) => {
    if (option && typeof option === 'object') {
      const meta = option.meta || {};
      const optionValues = normalizeComparableList([
        option.value,
        option.id,
        option.code,
        option.name,
        option.label,
        ...optionKeys.map((key) => option?.[key]),
        meta.value,
        meta.id,
        meta.code,
        meta.name,
        meta.label,
        ...metaKeys.map((key) => meta?.[key]),
      ]);

      return candidates.some((candidate) => optionValues.includes(candidate));
    }

    return candidates.includes(toComparable(option));
  }) || null;
};

export const getOptionCode = (option, { fallback = '' } = {}) => {
  if (!option) {
    return fallback;
  }

  const code = String(option?.meta?.code ?? option?.code ?? option?.value ?? '').trim();
  return code === '' ? fallback : code;
};

export const getOptionId = (option, { fallback = 0, positive = false } = {}) => {
  const parsed = Number(option?.meta?.id ?? option?.id ?? getOptionValue(option));

  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  if (positive) {
    return parsed > 0 ? parsed : fallback;
  }

  return parsed;
};

export const resolveOption = (options, rawValue) => {
  const normalized = getOptionValue(rawValue);

  if (normalized === null || normalized === undefined || normalized === '') {
    return null;
  }

  if (!Array.isArray(options) || options.length === 0) {
    return normalized;
  }

  const match = findOptionByRawValue(options, normalized);

  return match || normalized;
};

export const toPositiveOptionId = (rawValue, { fallback = 0 } = {}) => {
  const parsed = Number(getOptionValue(rawValue));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const resolveFirstPositiveOptionId = (...candidates) => {
  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = candidates[index];
    const normalizedId = getOptionId(candidate, { fallback: 0, positive: true });
    if (normalizedId > 0) {
      return normalizedId;
    }

    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return 0;
};

export const ensureOptionPresent = (optionsMap, optionKey, option) => {
  if (!option || typeof option !== 'object') {
    return {
      options: optionsMap,
      option,
    };
  }

  const list = Array.isArray(optionsMap?.[optionKey]) ? optionsMap[optionKey] : [];
  const existing = findOptionByRawValue(list, option);
  if (existing) {
    return {
      options: optionsMap,
      option: existing,
    };
  }

  return {
    options: {
      ...optionsMap,
      [optionKey]: [...list, option],
    },
    option,
  };
};
